import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import {
  FaUserFriends,
  FaUserCheck,
  FaUserTimes,
  FaRegCalendarCheck,
  FaCalendarTimes,
  FaBan,
  FaClock,
  FaBriefcase
} from 'react-icons/fa';
import api from '../../utils/api';

function HrDashboard() {
  const [counts, setCounts] = useState(null);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        const res = await api.get(`/api/hr-dashboard/counts`);
        setCounts(res.data);
      } catch (err) {
        console.error('Error fetching HR dashboard data:', err);
      }
    };
    fetchDashboardData();
  }, []);

  

  const card = (label, value, icon, color = 'text-blue-600') => (
    <motion.div
      className="flex items-center justify-between bg-gradient-to-r from-gray-100 to-gray-200 shadow rounded-xl p-4 sm:p-5 hover:shadow-md transition"
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
    >
      <div className="flex items-center gap-3">
        <div className={`text-lg sm:text-xl ${color}`}>{icon}</div>
        <div className="text-sm font-medium text-gray-600">{label}</div>
      </div>
      <div className="text-xl font-bold text-gray-800">{value}</div>
    </motion.div>
  );

  const skeletonCard = () => (
    <div className="flex items-center justify-between bg-gradient-to-r from-gray-100 to-gray-200 shadow rounded-xl p-4 sm:p-5 animate-pulse">
      <div className="flex items-center gap-3">
        <div className="w-6 h-6 bg-gray-300 rounded-full"></div>
        <div className="h-4 w-24 bg-gray-300 rounded"></div>
      </div>
      <div className="h-6 w-10 bg-gray-300 rounded"></div>
    </div>
  );


  if (!counts) {
    return (
      <div className="mx-auto px-4 py-10 space-y-6 w-full">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="bg-white rounded-2xl shadow-md p-5 space-y-4">
              <div className="flex justify-between">
                <div className="h-5 w-32 bg-gray-300 rounded animate-pulse"></div>
                <div className="h-6 w-20 bg-gray-300 rounded animate-pulse"></div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {skeletonCard()}
                {skeletonCard()}
                {skeletonCard()}
                {skeletonCard()}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="w-full px-4 py-10 space-y-6">
      
      {/* Detailed Sections */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 w-full">
        {/* Employee Status Section */}
        <div className="bg-white rounded-2xl shadow-md p-5 space-y-4 w-full">
          <div className='flex justify-between'>
          <h2 className="text-lg sm:text-xl font-semibold text-slate-700 mb-1">Employee Status</h2>
          <div className="text-sm text-gray-500 mb-2">
            Total Employees: <span className="font-semibold text-gray-800 text-2xl">{counts.totalEmployees}</span>
          </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {card('Active Employees', counts.activeEmployees, <FaUserCheck />, 'text-green-600')}
            {card('Inactive Employees', counts.inActiveEmployees, <FaUserTimes />, 'text-red-600')}
          </div>
        </div>

        {/* Leave Status Section */}
        <div className="bg-white rounded-2xl shadow-md p-5 space-y-4 w-full">
          <div className='flex justify-between'>
          <h2 className="text-lg sm:text-xl font-semibold text-slate-700 mb-1">Leave Status</h2>
          <div className="text-sm text-gray-500 mb-2">
            Total Leave Requests: <span className="font-semibold text-gray-800 text-2xl">{counts.totalLeaveRequests}</span>
          </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {card('Pending Leaves', counts.pendingLeavesRequests, <FaRegCalendarCheck />, 'text-yellow-500')}
            {card('Approved Leaves', counts.approvedLeavesRequests, <FaRegCalendarCheck />, 'text-green-500')}
            {card('Rejected Leaves', counts.rejectedLeavesRequests, <FaCalendarTimes />, 'text-red-500')}
            {card('Cancelled Leaves', counts.calcelledLeaveRequests, <FaBan />, 'text-gray-500')}
          </div>
        </div>

        {/* On Duty Status Section */}
        <div className="bg-white rounded-2xl shadow-md p-5 space-y-4 w-full">
          <div className='flex justify-between'>
          <h2 className="text-lg sm:text-xl font-semibold text-slate-700 mb-1">On Duty Status</h2>
          <div className="text-sm text-gray-500 mb-2">
            Total On Duty Requests: <span className="font-semibold text-gray-800 text-2xl">{counts.totalOnDutyRequests}</span>
          </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {card('Pending On Duty', counts.pendingOnDutyRequests, <FaBriefcase />, 'text-yellow-500')}
            {card('Approved On Duty', counts.approvedOnDutyRequests, <FaBriefcase />, 'text-green-500')}
            {card('Rejected On Duty', counts.rejectedOnDutyRequests, <FaBriefcase />, 'text-red-500')}
          </div>
        </div>

        {/* Comp Off Status Section */}
        <div className="bg-white rounded-2xl shadow-md p-5 space-y-4 w-full">
          <div className='flex justify-between'>
          <h2 className="text-lg sm:text-xl font-semibold text-slate-700 mb-1">Comp Off Status</h2>
          <div className="text-sm text-gray-500 mb-2">
            Total Comp Off Requests: <span className="font-semibold text-gray-800 text-2xl">{counts.totalCompOffRequests}</span>
          </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {card('Pending Comp Offs', counts.pendingCompOffRequests, <FaClock />, 'text-yellow-500')}
            {card('Approved Comp Offs', counts.approvedCompOffRequests, <FaClock />, 'text-green-500')}
            {card('Rejected Comp Offs', counts.rejectedCompOffRequests, <FaClock />, 'text-red-500')}
          </div>
        </div>
      </div>
    </div>
  );
}

export default HrDashboard;
